import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Check, Infinity, Clock, CaretRight, Star, Tag, Sparkle, Coins, Shield, HandCoins } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { getProgramConfig, BRAND, PLAN_ORDER, SUBSCRIPTION_PROGRAMS } from '../config/programs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscriptionsPage() {
  const { t, fmtDate } = useLanguage();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [cars, setCars] = useState([]);
  const [currentSub, setCurrentSub] = useState(null);
  const [unlimitedAvail, setUnlimitedAvail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedCar, setSelectedCar] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [showCashInfo, setShowCashInfo] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [seats, setSeats] = useState({});      // plan_id → {capacity, free, status, ...}
  const [waitlistJoining, setWaitlistJoining] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [plansRes, programsRes, carsRes, subRes, availRes, seatsRes] = await Promise.all([
          axios.get(`${API}/subscription-plans`),
          axios.get(`${API}/wash-programs`),
          axios.get(`${API}/cars`, { withCredentials: true }),
          axios.get(`${API}/subscriptions/my`, { withCredentials: true }),
          axios.get(`${API}/subscriptions/unlimited-availability`),
          axios.get(`${API}/subscription-seats`),
        ]);
        setPlans(plansRes.data);
        setPrograms(programsRes.data);
        setCars(carsRes.data);
        setCurrentSub(subRes.data);
        setUnlimitedAvail(availRes.data);
        const map = {};
        (seatsRes.data || []).forEach((s) => { map[s.plan_id] = s; });
        setSeats(map);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const joinWaitlist = async (planId) => {
    setWaitlistJoining(true);
    try {
      const { data } = await axios.post(`${API}/subscription-waitlist/join`, { plan_id: planId }, { withCredentials: true });
      if (data.already_in_waitlist) {
        toast.info(`${t('waitlistAlreadyIn')} #${data.position}`);
      } else {
        toast.success(`${t('waitlistJoined')} #${data.position}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setWaitlistJoining(false);
    }
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setShowDialog(true);
  };

  const handleSubscribe = async () => {
    if (!selectedCar) {
      toast.error(t('pleaseSelectCar'));
      return;
    }

    setSubscribing(true);
    try {
      await axios.post(`${API}/subscriptions/subscribe`, {
        plan_id: selectedPlan.plan_id,
        car_id: selectedCar
      }, { withCredentials: true });

      toast.success(t('success') + '!');
      setShowDialog(false);
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setSubscribing(false);
    }
  };

  const getPlanProgramName = (plan) => {
    return SUBSCRIPTION_PROGRAMS[plan.plan_id] || 'Standard';
  };

  const getProgramPrice = (programName) => {
    const program = programs.find(p => p.name_en === programName);
    return program?.price || 0;
  };

  const calculateSavings = (plan) => {
    const programName = getPlanProgramName(plan);
    const singlePrice = getProgramPrice(programName);
    const normalTotal = singlePrice * plan.washes_included;
    const savings = normalTotal - plan.price;
    const savingsPercent = normalTotal > 0 ? Math.round((savings / normalTotal) * 100) : 0;
    return { normalTotal, savings, savingsPercent };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] safe-bottom flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#0ABAB5] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // B10 — plans now include 8 washes; read the real total from the plan (fallback 8).
  const subTotalWashes = currentSub
    ? (plans.find((p) => p.plan_id === currentSub.plan_id)?.washes_included
       || Math.max(8, currentSub.washes_remaining || 0))
    : 0;

  return (
    <div className="min-h-screen bg-[#050505] safe-bottom">
      {/* Header */}
      <div className="p-4 pt-6">
        <h1 className="text-2xl font-bold tracking-tight font-['Outfit'] text-white">{t('subscriptionPlans')}</h1>
        <p className="text-white/50 mt-1">{t('mainSlogan')}</p>
      </div>

      {/* Current Subscription with Usage Stats */}
      {currentSub && (
        <motion.div
          className="mx-4 mb-4"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="relative overflow-hidden rounded-3xl">
            <div className="absolute inset-0 bg-gradient-to-br from-[#0ABAB5]/20 via-transparent to-transparent" />
            <div className="relative p-5 bg-[#0a0a0a] border border-[#0ABAB5]/30 rounded-3xl">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-[#0ABAB5] text-[10px] font-bold uppercase tracking-wider">{t('currentPlan')}</p>
                  <p className="text-xl font-bold font-['Outfit'] text-white">{currentSub.plan_name}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-[#0ABAB5]/20 flex items-center justify-center">
                  <Star size={20} weight="fill" className="text-[#0ABAB5]" />
                </div>
              </div>
              
              {!currentSub.is_unlimited ? (
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/60">{t('washesIncluded')}</span>
                    <span className="font-semibold text-white">{subTotalWashes} {t('washes')}</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-xl bg-white/5 text-center">
                      <p className="text-2xl font-bold text-white/60">{Math.max(0, subTotalWashes - currentSub.washes_remaining)}</p>
                      <p className="text-[10px] text-white/40">{t('washesUsed')}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-[#0ABAB5]/10 text-center">
                      <p className="text-2xl font-bold text-[#0ABAB5]">{currentSub.washes_remaining}</p>
                      <p className="text-[10px] text-[#0ABAB5]/70">{t('washesRemaining')}</p>
                    </div>
                  </div>
                  
                  {/* Visual Progress */}
                  <div className="flex gap-1.5">
                    {[...Array(subTotalWashes)].map((_, i) => (
                      <div 
                        key={i}
                        className={`flex-1 h-2 rounded-full transition-all ${
                          i < (subTotalWashes - currentSub.washes_remaining) 
                            ? 'bg-white/20' 
                            : 'bg-[#0ABAB5]'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5">
                  <Infinity size={28} weight="bold" className="text-[#0ABAB5]" />
                  <div>
                    <p className="font-semibold text-white">{t('unlimited')}</p>
                    <p className="text-white/50 text-sm">{t('everyHours')}</p>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-white/[0.06]">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-white/40">{t('subValidFrom')}</p>
                  <p className="text-xs text-white/80 mt-0.5">{fmtDate(currentSub.created_at)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-white/40">{t('subValidTo')}</p>
                  <p className="text-xs text-white/80 mt-0.5">{fmtDate(currentSub.expires_at)}</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Plans List */}
      <div className="px-4 space-y-4 pb-8">
        {[...plans].sort((a, b) => (PLAN_ORDER[a.plan_id] || 99) - (PLAN_ORDER[b.plan_id] || 99)).map((plan, index) => {
          const isUnlimited = plan.is_unlimited;
          const isPopular = plan.name_en === 'Standard Pass';
          const isBestValue = plan.name_en === 'Larje Pa Limit' || plan.name_en === 'Unlimited Pass';
          const programName = getPlanProgramName(plan);
          const config = getProgramConfig(programName);
          const { normalTotal, savings, savingsPercent } = calculateSavings(plan);
          
          return (
            <motion.div
              key={plan.plan_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <div 
                className={`relative rounded-3xl border overflow-hidden transition-all ${
                  isPopular 
                    ? 'border-2 border-[#FF5A00]/50' 
                    : isBestValue
                    ? 'border-2 border-[#0ABAB5]/50'
                    : 'border border-white/5'
                }`}
                style={{
                  background: isPopular 
                    ? `linear-gradient(135deg, rgba(255, 90, 0, 0.1) 0%, transparent 100%)` 
                    : isBestValue
                    ? `linear-gradient(135deg, rgba(10, 186, 181, 0.1) 0%, transparent 100%)`
                    : '#0a0a0a'
                }}
              >
                {/* TOP BANNER — Iter18 Task 1. Crown + text. Luxury has no banner. */}
                {(isPopular || isBestValue) && (
                  <div
                    className={`relative w-full px-3 py-2 flex items-center justify-center gap-1.5 ${
                      isBestValue
                        ? 'bg-gradient-to-r from-[#FFB400] via-[#FF7A2C] to-[#FF5A00]'
                        : 'bg-gradient-to-r from-[#FF5A00] via-[#FF7A2C] to-[#FF5A00]'
                    }`}
                    data-testid={`badge-most-popular-${plan.plan_id}`}
                  >
                    <span className="text-base leading-none" role="img" aria-label="crown">👑</span>
                    <span className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-white drop-shadow-sm">
                      {isBestValue ? t('theBestUnlimited') : t('mostPopular')}
                    </span>
                  </div>
                )}

                {/* Iter17 Task 2 — Removed the static "X / Y vende të lira" callout
                    above Larje Pa Limit. The dynamic seat-indicator below replaces it. */}

                {/* Seat scarcity bar (Task 18 — iter13 wave3). All plans get a color-coded
                    free-seats indicator that turns yellow → red → waitlist as the value drops. */}
                {seats[plan.plan_id] && (() => {
                  const s = seats[plan.plan_id];
                  const status = s.status;
                  const color = status === 'full'   ? '#ef4444'
                              : status === 'danger' ? '#ef4444'
                              : status === 'warn'   ? '#FFD700'
                              : '#22C55E';
                  const labelKey = status === 'full'   ? 'seatsFull'
                                 : status === 'danger' ? 'seatsDanger'
                                 : status === 'warn'   ? 'seatsWarn'
                                 : 'seatsOk';
                  const prefix = status === 'danger' ? '🔴 ' : status === 'warn' ? '⚠️ ' : '';
                  return (
                    <div
                      className="px-4 py-2 text-xs font-semibold border-b border-white/5 flex items-center justify-between"
                      style={{ background: `${color}15`, color }}
                      data-testid={`seat-indicator-${plan.plan_id}`}
                    >
                      <span>
                        {status === 'full'
                          ? `${prefix}${t('seatsFull')}`
                          : `${prefix}${t(labelKey).replace('{n}', s.free)}`}
                      </span>
                      {status === 'full' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); joinWaitlist(plan.plan_id); }}
                          disabled={waitlistJoining}
                          className="h-6 px-2 text-[10px] rounded-md border-white/20 text-white hover:text-white"
                          data-testid={`waitlist-join-${plan.plan_id}`}
                        >
                          {t('joinWaitlist')}
                        </Button>
                      )}
                    </div>
                  );
                })()}

                <div className="p-5">
                  <div className="flex items-start gap-4 mb-4">
                    {/* Car Image */}
                    {/* Iter18 Task 1 — All sub cards use config.carImage (Standard Pass +
                        Larje Pa Limit pick Fast_3.png via their Standard mapping). scale-125
                        enlarges the car inside the existing slot. */}
                    <div 
                      className="w-20 h-16 rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center"
                      style={{ backgroundColor: config.colorDark }}
                    >
                      <img 
                        src={config.carImage} 
                        alt={programName}
                        className="w-full h-full object-contain scale-125"
                        loading="lazy"
                      />
                    </div>

                    <div className="flex-1">
                      <h3 className="text-lg font-bold font-['Outfit'] text-white">{plan.name_en}</h3>
                      <div className="flex items-baseline gap-1 mt-1">
                        <span className="text-3xl font-bold" style={{ color: isPopular ? BRAND.secondary : BRAND.primary }}>
                          {plan.price.toLocaleString()}
                        </span>
                        <span className="text-white/40 text-sm">{t('lek')}{t('perMonth')}</span>
                      </div>
                    </div>
                  </div>

                  {/* Savings Callout + per-wash price */}
                  {!isUnlimited && plan.washes_included > 0 && (
                    <div className="mb-4 p-3 rounded-xl bg-green-500/10 border border-green-500/20 space-y-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-white/50">{t('normalPrice')}: <span className="line-through">{normalTotal.toLocaleString()} {t('lek')}</span></p>
                          <p className="text-xs text-white/50">{t('subscriptionPrice')}: <span className="text-green-400 font-semibold">{plan.price.toLocaleString()} {t('lek')}</span></p>
                        </div>
                        <div className="text-right">
                          <p className="text-green-400 font-bold text-lg">{t('youSave')} {savingsPercent}%</p>
                          <p className="text-green-400/70 text-xs">{savings.toLocaleString()} {t('lek')}</p>
                        </div>
                      </div>
                      <p className="text-[#FF5A00] text-sm font-bold pt-1 border-t border-white/5">
                        {t('perWashOnly').replace('{price}', Math.round(plan.price / plan.washes_included).toLocaleString())}
                      </p>
                    </div>
                  )}

                  {/* Washes Info */}
                  <div className="flex items-center gap-2 mb-4 p-3 rounded-xl bg-white/5">
                    {isUnlimited ? (
                      <>
                        <Infinity size={24} className="text-[#0ABAB5]" />
                        <div>
                          <span className="font-semibold text-white">{t('unlimited')}</span>
                          <span className="text-white/50 text-sm ml-2">({t('everyHours')})</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div 
                          className="w-10 h-10 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: config.colorLight }}
                        >
                          <span className="text-xl font-bold" style={{ color: config.color }}>{plan.washes_included}</span>
                        </div>
                        <div>
                          <span className="font-semibold text-white">{plan.washes_included} {t('washes')}</span>
                          <span className="text-white/50 text-sm ml-1">• {programName}</span>
                        </div>
                      </>
                    )}
                  </div>

                  {isUnlimited && plan.cooldown_hours && (
                    <div className="space-y-1 mb-4">
                      <div className="flex items-center gap-2 text-sm text-white/50">
                        <Clock size={16} />
                        <span>Limit: {t('everyHours').replace('24', plan.cooldown_hours)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-white/50">
                        <Check size={16} weight="bold" className="text-[#0ABAB5]" />
                        <span>{t('unlimitedVehicleNote')}</span>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Check size={16} weight="bold" className="text-[#0ABAB5]" />
                      <span className="text-white/70">{t('plateRecognitionShort')}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Check size={16} weight="bold" className="text-[#0ABAB5]" />
                      <span className="text-white/70">{t('autoEntry')}</span>
                    </div>
                    {/* MASTER FIX B12 — perfume exists ONLY on interior wash, removed from Luxury */}
                  </div>

                  {/* Monthly expiration notice */}
                  <div className="mb-4 p-2.5 rounded-xl bg-white/5 border border-white/5 text-[11px] text-white/55 leading-snug">
                    <span className="text-white/75 font-semibold">{t('monthlySubscription')}.</span> {t('unusedWashesNotice')}
                  </div>

                  {isUnlimited && unlimitedAvail?.remaining === 0 ? (
                    <Button
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/subscriptions/waitlist`, {}, { withCredentials: true });
                          toast.success(t('onWaitlist'));
                        } catch (err) {
                          toast.error('Failed to join waitlist');
                        }
                      }}
                      className="w-full h-12 rounded-2xl font-semibold bg-white/10 text-white hover:bg-white/15"
                      data-testid={`waitlist-${plan.plan_id}`}
                    >
                      {t('joinWaitlist')}
                    </Button>
                  ) : (
                    /* Iter16 Task 2 — Animated conic-gradient border on Abonohu button
                       for Standard Pass, Luxury Pass, Larje Pa Limit. Same effect as the
                       cash CTA in the program-single modal. */
                    (() => {
                      const isLuxuryPlan = plan.name_en === 'Luxury Pass';
                      const showAnim = isPopular || isBestValue || isLuxuryPlan;
                      if (showAnim) {
                        return (
                          <div className="cash-cta-animated rounded-2xl" data-testid={`subscribe-wrapper-${plan.plan_id}`}>
                            <div className="cash-cta-border" aria-hidden="true" />
                            <Button
                              onClick={() => handleSelectPlan(plan)}
                              className="cash-cta-inner relative w-full h-12 rounded-2xl font-semibold text-white"
                              data-testid={`subscribe-${plan.plan_id}`}
                            >
                              {t('subscribePlan')}
                            </Button>
                          </div>
                        );
                      }
                      return (
                        <Button
                          onClick={() => handleSelectPlan(plan)}
                          className="relative w-full h-12 rounded-2xl font-semibold transition-all cta-button-primary"
                          data-testid={`subscribe-${plan.plan_id}`}
                        >
                          {t('subscribePlan')}
                        </Button>
                      );
                    })()
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Subscribe Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent
          className="bg-[#121212] border-white/10 max-w-md mx-auto"
          style={selectedPlan ? {
            borderColor: `${getProgramConfig(getPlanProgramName(selectedPlan)).color}55`,
            boxShadow: `0 0 36px ${getProgramConfig(getPlanProgramName(selectedPlan)).colorLight}`,
          } : undefined}
          data-testid="subscribe-dialog"
        >
          <DialogHeader>
            <div className="flex items-center gap-3">
              {selectedPlan && (
                <span
                  className="inline-block w-3 h-8 rounded-full"
                  style={{
                    background: selectedPlan.is_unlimited
                      ? `linear-gradient(180deg, #0ABAB5, #FF5A00)`
                      : getProgramConfig(getPlanProgramName(selectedPlan)).color,
                  }}
                  data-testid="dialog-plan-color"
                />
              )}
              <DialogTitle
                className="text-xl font-['Outfit']"
                style={selectedPlan ? {
                  color: selectedPlan.is_unlimited
                    ? '#0ABAB5'
                    : getProgramConfig(getPlanProgramName(selectedPlan)).color,
                } : undefined}
              >
                {selectedPlan?.name_en}
              </DialogTitle>
            </div>
            <DialogDescription className="text-white/50">
              {selectedPlan?.is_unlimited 
                ? `${t('unlimited')} (${t('everyHours')})` 
                : `${selectedPlan?.washes_included} ${t('washes')} ${t('perMonth')}`
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Iter17 Task 3 — One-plate warning shown right above the plate picker. */}
            <div className="px-3 py-2 rounded-xl bg-[#FFD700]/10 border border-[#FFD700]/30 flex items-start gap-2" data-testid="subscription-one-plate-warning">
              <Shield size={14} weight="fill" className="text-[#FFD700] mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-[#FFD700]/90 font-medium leading-snug">
                {t('subOnePlateWarning')}
              </p>
            </div>

            {/* Car Selection */}
            <div>
              <label className="text-sm text-white/60 mb-2 block">{t('selectCar')}</label>
              {cars.length === 0 ? (
                <Button
                  variant="outline"
                  onClick={() => navigate('/my-car')}
                  className="w-full h-12 border-white/10 bg-white/5"
                >
                  {t('addCar')} <CaretRight size={16} className="ml-2" />
                </Button>
              ) : (
                <div className="space-y-2">
                  {cars.map(car => (
                    <button
                      key={car.car_id}
                      onClick={() => setSelectedCar(car.car_id)}
                      className={`w-full p-3 rounded-xl border text-left transition-all ${
                        selectedCar === car.car_id 
                          ? 'border-[#0ABAB5] bg-[#0ABAB5]/10' 
                          : 'border-white/10 bg-white/5'
                      }`}
                    >
                      <span className="font-mono font-medium text-white">{car.license_plate}</span>
                      {car.make && <span className="text-white/50 ml-2">{car.make} {car.model}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Total + payment options. Card button is rendered ONCE (was duplicated). */}
            <div className="pt-4 border-t border-white/10">
              <div className="flex justify-between items-center mb-4">
                <span className="text-white/60">{t('perMonth')}</span>
                <span
                  className="text-3xl font-bold"
                  style={{ color: selectedPlan
                    ? (selectedPlan.is_unlimited ? '#0ABAB5' : getProgramConfig(getPlanProgramName(selectedPlan)).color)
                    : '#0ABAB5' }}
                >
                  {selectedPlan?.price?.toLocaleString()} {t('lek')}
                </span>
              </div>

              {/* Cash payment FIRST — primary CTA (Owner request: clickable without
                  car selection, opens info popup; no auto-activation). Orange button
                  with animated rotating border highlights the cash-first preference. */}
              <button
                onClick={() => setShowCashInfo(true)}
                className="cash-cta-animated relative w-full h-14 rounded-2xl mb-3 overflow-hidden text-white font-semibold flex items-center justify-center gap-2"
                data-testid="pay-cash-info-btn"
              >
                <span className="cash-cta-border" aria-hidden="true" />
                <span className="cash-cta-inner">
                  {/* Iter18 Task 3 — Same hand-with-money animation as the popup. */}
                  <span className="cash-btn-anim" aria-hidden="true">
                    <HandCoins size={20} weight="fill" />
                    <span className="cash-btn-bill cash-btn-bill-a" />
                    <span className="cash-btn-bill cash-btn-bill-b" />
                  </span>
                  {t('payWithCashStaff')}
                </span>
              </button>

              {/* Stripe card payment REMOVED — not available for Albanian entities.
                  Payments: cash/POS on site + manual activation by admin. */}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cash payment INFO popup — no action, just instructs the customer to visit staff. */}
      <Dialog open={showCashInfo} onOpenChange={setShowCashInfo}>
        <DialogContent
          className="bg-[#121212] border-[#FFD700]/30 max-w-sm mx-auto"
          data-testid="cash-info-dialog"
        >
          <DialogHeader>
            {/* Iter17 Task 3 — Animated hand-with-money (CSS-only, no external GIF).
                Hand at the base, three banknotes float up and fade out continuously. */}
            <div className="w-20 h-20 mx-auto relative mb-2" data-testid="cash-info-animation">
              <div className="cash-anim-stage">
                <div className="cash-anim-hand">
                  <HandCoins size={48} weight="fill" />
                </div>
                <div className="cash-anim-bill cash-anim-bill-a" />
                <div className="cash-anim-bill cash-anim-bill-b" />
                <div className="cash-anim-bill cash-anim-bill-c" />
              </div>
            </div>
            <DialogTitle className="text-center text-lg font-['Outfit']">
              {t('payWithCashStaff')}
            </DialogTitle>
          </DialogHeader>
          <p className="text-center text-white/70 text-sm leading-relaxed px-2 mt-1">
            {t('cashStaffInfo')}
          </p>
          <Button
            onClick={() => setShowCashInfo(false)}
            className="w-full h-12 cta-button-primary rounded-2xl mt-3"
            data-testid="cash-info-close"
          >
            OK
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
